import React, { useEffect, useRef, useState } from 'react';

// Adds `.in` once the element enters the viewport.
export const Reveal = ({ as: Tag = 'div', delay = 0, className = '', children, ...rest }) => {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    const io = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) { el.classList.add('in'); io.disconnect(); }
    }, { threshold: 0.15, rootMargin: '0px 0px -8% 0px' });
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return <Tag ref={ref} className={`reveal ${className}`} style={{ '--d': `${delay}ms` }} {...rest}>{children}</Tag>;
};

// Counts "500+", "99.9%", "24/7" style values up when scrolled into view.
export const CountUp = ({ value }) => {
  const m = String(value).match(/^([\d.]+)(.*)$/);
  const ref = useRef(null);
  const [n, setN] = useState(0);
  useEffect(() => {
    if (!m || value.includes('/')) return;
    const target = parseFloat(m[1]);
    const io = new IntersectionObserver(([e]) => {
      if (!e.isIntersecting) return;
      io.disconnect();
      const t0 = performance.now();
      const tick = (t) => {
        const p = Math.min((t - t0) / 1600, 1);
        setN(target * (1 - Math.pow(1 - p, 4)));
        if (p < 1) requestAnimationFrame(tick);
      };
      requestAnimationFrame(tick);
    }, { threshold: 0.5 });
    io.observe(ref.current);
    return () => io.disconnect();
    // eslint-disable-next-line
  }, [value]);
  if (!m || value.includes('/')) return <span ref={ref}>{value}</span>;
  const dec = m[1].includes('.') ? 1 : 0;
  return <span ref={ref}>{n.toFixed(dec)}{m[2]}</span>;
};

// onMouseMove handler feeding --mx/--my to .spot cards.
export const spotlight = (e) => {
  const r = e.currentTarget.getBoundingClientRect();
  e.currentTarget.style.setProperty('--mx', `${e.clientX - r.left}px`);
  e.currentTarget.style.setProperty('--my', `${e.clientY - r.top}px`);
};

// Hero background: red glow that eases toward the pointer.
export const PointerGlow = () => {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    const parent = el.parentElement;
    let tx = 0, ty = 0, x = 0, y = 0, raf;
    const move = (e) => { const r = parent.getBoundingClientRect(); tx = e.clientX - r.left; ty = e.clientY - r.top; };
    const loop = () => { x += (tx - x) * 0.08; y += (ty - y) * 0.08; el.style.transform = `translate(${x - 300}px, ${y - 300}px)`; raf = requestAnimationFrame(loop); };
    parent.addEventListener('pointermove', move);
    raf = requestAnimationFrame(loop);
    return () => { parent.removeEventListener('pointermove', move); cancelAnimationFrame(raf); };
  }, []);
  return <div ref={ref} aria-hidden className="absolute top-0 left-0 w-[600px] h-[600px] rounded-full pointer-events-none" style={{ background: 'radial-gradient(circle, rgba(236,40,32,0.22), transparent 65%)' }} />;
};

// The Q mark, drawn on load: pill outline, then the diagonal slash.
export const QMark = ({ className = '' }) => (
  <svg viewBox="0 0 360 460" fill="none" className={className} aria-hidden>
    <defs>
      <filter id="qblur"><feGaussianBlur stdDeviation="14" /></filter>
    </defs>
    <g className="q-glow" filter="url(#qblur)" stroke="#ec2820" strokeWidth="14" strokeLinecap="round">
      <rect x="50" y="40" width="230" height="350" rx="115" />
      <path d="M50 190 L300 400" />
    </g>
    <rect className="q-draw" x="50" y="40" width="230" height="350" rx="115" stroke="#ec2820" strokeWidth="12" pathLength="1400" />
    <path className="q-slash" d="M40 200 L310 420" strokeWidth="2" pathLength="520" strokeLinecap="round" style={{ stroke: 'var(--paper)' }} />
    <path className="q-slash" d="M60 170 L320 385" stroke="#ec2820" strokeWidth="12" pathLength="520" strokeLinecap="round" style={{ animationDelay: '1.7s' }} />
  </svg>
);

// Calls cb(progress 0..1) while a tall section scrolls past (for sticky/pinned scenes).
export const useScrollScene = (ref, cb) => {
  const saved = useRef(cb);
  saved.current = cb;
  useEffect(() => {
    const el = ref.current;
    let raf = 0;
    const run = () => {
      raf = 0;
      const r = el.getBoundingClientRect();
      const span = r.height - window.innerHeight;
      saved.current(span > 0 ? Math.min(1, Math.max(0, -r.top / span)) : 0, r);
    };
    const on = () => { if (!raf) raf = requestAnimationFrame(run); };
    run();
    window.addEventListener('scroll', on, { passive: true });
    window.addEventListener('resize', on);
    return () => { window.removeEventListener('scroll', on); window.removeEventListener('resize', on); cancelAnimationFrame(raf); };
  }, [ref]);
};

// Full-width ruler under the navbar: ticks, a sweeping glint, and a red progress line whose
// head carries the scroll position as an 8-bit binary number.
export const NavRule = () => {
  const fill = useRef(null), head = useRef(null), bits = useRef(null);
  useEffect(() => {
    const on = () => {
      const d = document.documentElement;
      const p = d.scrollTop / Math.max(1, d.scrollHeight - d.clientHeight);
      fill.current.style.transform = `scaleX(${p})`;
      head.current.style.left = `${p * 100}%`;
      bits.current.textContent = Math.round(p * 255).toString(2).padStart(8, '0');
    };
    on();
    window.addEventListener('scroll', on, { passive: true });
    window.addEventListener('resize', on);
    return () => { window.removeEventListener('scroll', on); window.removeEventListener('resize', on); };
  }, []);
  return (
    <div className="nav-rule" aria-hidden>
      <div className="nav-ticks" />
      <div className="nav-glint" />
      <div ref={fill} className="absolute top-0 left-0 right-0 h-px bg-[var(--red)] origin-left" style={{ transform: 'scaleX(0)' }} />
      <div ref={head} className="absolute top-0 -translate-x-1/2 -translate-y-1/2 flex flex-col items-center" style={{ left: 0 }}>
        <span className="w-2 h-2 rotate-45 bg-[var(--red)] shadow-[0_0_12px_var(--red)]" />
        <span ref={bits} className="font-mono-label !text-[0.55rem] text-[var(--red)] mt-2 bg-[var(--glass)] px-1 rounded-sm backdrop-blur-sm hidden sm:block">00000000</span>
      </div>
    </div>
  );
};

// Ruler line between sections: draws in left to right, with the section index in binary at the end.
export const SectionRule = ({ index = '00', label = '' }) => {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    const io = new IntersectionObserver(([e]) => { if (e.isIntersecting) { el.classList.add('in'); io.disconnect(); } }, { threshold: 0.6 });
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return (
    <div ref={ref} className="section-rule" aria-hidden>
      <div className="section-rule-line" />
      <div className="nav-ticks" />
      <span className="section-rule-mark" />
      <p className="font-mono-label section-rule-label flex justify-between max-w-6xl mx-auto px-6 text-[var(--muted)]">
        <span><span className="text-[var(--red)]">{index}</span>{label && ` / ${label}`}</span>
        <span>{parseInt(index, 10).toString(2).padStart(8, '0')}</span>
      </p>
    </div>
  );
};
